import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  LineChart,
  Cloud,
  Building,
  Wallet,
  BookOpen,
  Coins,
  Receipt,
  UserPlus,
  Package,
  Search,
  FileText,
  type LucideIcon,
} from "lucide-react";

import { listCategorias, listShortcuts, type Shortcut } from "@/api/herramientas";

const ICON_MAP: Record<string, LucideIcon> = {
  "line-chart": LineChart,
  cloud: Cloud,
  building: Building,
  wallet: Wallet,
  book: BookOpen,
  "cash-register": Coins,
  receipt: Receipt,
  "user-plus": UserPlus,
  box: Package,
  search: Search,
};

const NAVY = "#001f3f";

interface ShortcutCardProps {
  shortcut: Shortcut;
}

const ShortcutCard = ({ shortcut }: ShortcutCardProps) => {
  const Icon = ICON_MAP[shortcut.icon] ?? FileText;
  const badge = shortcut.badge;
  const inner = (
    <div className="group flex min-h-[116px] flex-col items-center justify-start rounded-lg px-2 pt-2 pb-2 text-center text-ink-primary transition-all duration-200 hover:-translate-y-1 hover:bg-accent-blue-light/40 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue">
      <div
        className="relative mb-3 flex h-[67px] w-[67px] flex-shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-accent-blue"
      >
        <Icon className="h-7 w-7" strokeWidth={1.6} />
        {badge ? (
          <span className="absolute -right-3 bottom-2 rounded-[10px] bg-[#ff9418] px-2 py-0.5 text-[10px] font-bold uppercase text-white">
            {badge}
          </span>
        ) : null}
      </div>
      <span className="max-w-[220px] text-sm font-semibold leading-snug">
        {shortcut.title}
      </span>
    </div>
  );

  if (shortcut.external) {
    return (
      <a
        href={shortcut.to}
        target="_blank"
        rel="noopener noreferrer"
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue rounded-lg"
      >
        {inner}
      </a>
    );
  }

  return (
    <Link to={shortcut.to} className="rounded-lg">
      {inner}
    </Link>
  );
};

export const HerramientasPage = () => {
  const { data: categorias = [] } = useQuery({
    queryKey: ["herramientas", "categorias"],
    queryFn: listCategorias,
    staleTime: 60 * 1000,
    refetchOnMount: "always",
  });

  const { data: shortcuts = [], isLoading } = useQuery({
    queryKey: ["herramientas", "shortcuts"],
    queryFn: listShortcuts,
    staleTime: 60 * 1000,
    refetchOnMount: "always",
  });

  const tabActiva = categorias[0]?.label ?? "Accesos directos";

  return (
    <div className="min-h-full">
      <nav
        aria-label="Pestañas del módulo Herramientas"
        className="flex h-[55px] items-end gap-2 border-b pl-2"
        style={{ borderColor: NAVY, backgroundColor: "#ffffff" }}
      >
        <div
          className={clsx(
            "flex h-[50px] items-center rounded-t-md border bg-white px-3 text-sm font-semibold",
          )}
          style={{
            borderColor: NAVY,
            borderBottomColor: "#ffffff",
            color: NAVY,
          }}
        >
          G&amp;D CONSULTING GROUP
        </div>
        <Link
          to="/"
          aria-label="Ir al dashboard principal"
          className={clsx(
            "flex h-[50px] items-center rounded-t-md border border-transparent bg-transparent px-3 text-sm font-semibold",
            "hover:border-accent-blue hover:text-accent-blue",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue",
          )}
          style={{ color: NAVY }}
        >
          {tabActiva}
        </Link>
      </nav>

      <main
        className="mt-8 overflow-hidden rounded-t-lg border border-slate-200 bg-bg-secondary"
      >
        <header className="flex min-h-[56px] items-center border-b border-slate-200 px-7">
          <h1 className="text-[21px] font-bold text-ink-primary">
            Te damos la bienvenida, ¿Qué deseas hacer?
          </h1>
        </header>

        {isLoading ? (
          <div className="px-7 py-10 text-sm text-ink-secondary">Cargando accesos directos…</div>
        ) : shortcuts.length === 0 ? (
          <div className="px-7 py-10 text-sm text-ink-secondary">
            No hay accesos directos configurados.
          </div>
        ) : (
          <section
            aria-label="Accesos directos"
            className="grid grid-cols-1 gap-x-6 gap-y-10 px-9 py-4 pb-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          >
            {shortcuts.map((s) => (
              <ShortcutCard key={s.id} shortcut={s} />
            ))}
          </section>
        )}
      </main>
    </div>
  );
};
